import { Link } from "react-router-dom";
import { Quote, Star, ArrowRight, ShieldCheck } from "lucide-react";
import { TESTIMONIALS } from "@/lib/constants";
import usePageMeta from "@/hooks/usePageMeta";

export default function Stories() {
  usePageMeta({
    title: "Stories & Testimonials, Real Navica Participants",
    description: "Real experiences from NDIS participants, families and providers who've worked with Navica Supports across Sydney.",
  });
  return (
    <div data-testid="stories-page">
      {/* HERO */}
      <section className="pt-16 lg:pt-24 pb-12 relative overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#EA823A] font-semibold mb-5 fade-up">Participant & referrer stories</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-[#1F2A2E] leading-[1.05] mb-6 fade-up fade-up-1">
            The work shows up in the <span className="italic" style={{ color: "var(--navica-coral)" }}>relationships</span>.
          </h1>
          <p className="text-xl text-[#4A595E] leading-relaxed max-w-3xl fade-up fade-up-2">
            Real reflections from the people we walk alongside. Some names are abbreviated and details are anonymised, always with the storyteller's permission.
          </p>
        </div>
      </section>

      {/* TESTIMONIALS GRID */}
      <section className="pb-24" data-testid="stories-grid">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, idx) => (
              <article
                key={t.id}
                className="card-soft fade-up flex flex-col"
                style={{ animationDelay: `${idx * 0.06}s` }}
                data-testid={`story-${t.id}`}
              >
                <Quote className="w-8 h-8 text-[#EA823A] mb-5" />
                <p className="text-[#1F2A2E] leading-relaxed mb-6 flex-1 font-display text-lg">"{t.quote}"</p>
                <div className="pt-5 border-t border-[#F0E5DE]">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star key={`${t.id}-star-${i}`} className="w-4 h-4 fill-[#EA823A] text-[#EA823A]" />
                    ))}
                  </div>
                  <div className="font-semibold text-[#1F2A2E]">{t.author}</div>
                  <div className="text-sm text-[#6B7A80]">{t.role}</div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CONSENT BANNER */}
      <section className="pb-24" data-testid="consent-banner">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="bg-[#F1E4D5] border border-[#F0E5DE] rounded-[2.5rem] p-8 lg:p-10 flex flex-col md:flex-row items-start gap-5">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-[#E2718A]" />
            </div>
            <div>
              <h3 className="font-display text-2xl text-[#1F2A2E] mb-3">Stories shared with consent.</h3>
              <p className="text-[#4A595E] leading-relaxed">
                Every quote on this page has been shared with the storyteller's written consent. Identifying details are removed unless explicitly approved. Participants can request a quote be removed at any time by emailing{" "}
                <a href="mailto:info@navicasupports.com" className="text-[#E2718A] underline">info@navicasupports.com</a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24" data-testid="stories-cta">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-display text-4xl md:text-5xl text-[#1F2A2E] leading-tight mb-6">
            Want to share <span className="italic" style={{ color: "var(--navica-coral)" }}>your</span> story?
          </h2>
          <p className="text-lg text-[#4A595E] leading-relaxed mb-8">
            Stories help other participants and families feel less alone. If you've worked with us and would like to share your experience, anonymously or named, we'd be honoured.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href="mailto:info@navicasupports.com?subject=I'd%20like%20to%20share%20my%20story" className="btn-primary">Share a story <ArrowRight className="w-4 h-4" /></a>
            <Link to="/referrals" className="btn-outline">Make a referral</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
